import { ACCOUNT_API_COMPANIES } from 'apiRoutes';
import {
	useSuggestedCompanies,
	SuggestedCompaniesMatchResult,
} from 'components/companies/AssignToCompanyForm/useSuggestedCompanies';
import { MontelAccount } from 'domain/account/interfaces';
import { Company } from 'domain/company/Company';
import { Subscription } from 'domain/subscription/interfaces/Subscription';
import { useGetOnMount } from 'hooks/useGetOnMount';

import {
	FunctionComponent,
	ReactNode,
	createContext,
	useContext,
	useEffect,
	useState,
} from 'react';

export type AssignToCompanyFormContextType = {
	suggestions: SuggestedCompaniesMatchResult;
	selectedCompany?: Company;
	updateSuggestions: () => void;
	setSelectedCompany: (company: Company | undefined) => void;
	subscriptions?: Subscription[];
};

export const AssignToCompanyFormContext =
	createContext<AssignToCompanyFormContextType | null>(null);

const AssignToCompanyFormContextProvider: FunctionComponent<{
	children: ReactNode;
	account: MontelAccount;
	subscriptions?: Subscription[];
}> = ({ children, account, subscriptions }) => {
	const [selectedCompany, setSelectedCompany] = useState<Company>();

	const { data: companyOptions } = useGetOnMount<Company[]>(
		`${ACCOUNT_API_COMPANIES}/GetCompanies`,
	);

	const { suggestions, refreshSuggestions } = useSuggestedCompanies(
		companyOptions,
		account,
	);

	useEffect(() => {
		if (!companyOptions || !account) return;

		updateSuggestions();
	}, [companyOptions, account, selectedCompany]);

	const updateSuggestions = () => {
		refreshSuggestions(companyOptions);
	};

	return (
		<AssignToCompanyFormContext.Provider
			value={{
				suggestions,
				selectedCompany,
				updateSuggestions,
				setSelectedCompany,
				subscriptions,
			}}
		>
			{children}
		</AssignToCompanyFormContext.Provider>
	);
};

export function useAssignToCompanyFormContext() {
	const context = useContext(AssignToCompanyFormContext);

	if (!context) {
		throw new Error(
			'useAssignToCompanyFormContext must be used within the AssignToCompanyFormContextProvider',
		);
	}

	return context;
}

export default AssignToCompanyFormContextProvider;
